/**
 * GoalPlacementOverlay — PMA-COCK-185. Moves the goal-frame capture target
 * from the post-shot panel below the pitch (retired here, see
 * `RefinementChipPanel.tsx`) to a pitch-scoped modal drawn OVER the field
 * itself, so the operator checks the target and taps where the ball went
 * without ever scrolling (client ask, 2026-09-11,
 * `specs/2026-09-11-goal-mouth-placement-overlay-design.md`). Mounted
 * through `TacticalField`'s `overlay` slot the moment an eligible Shot is
 * confirmed (`ActionStage.tsx`: `chipState.eventType === "Shot" &&
 * isPlacementEligible(chipState.event)` — the exact condition
 * `RefinementChipPanel.tsx` used to gate its own, now-retired, goal-frame
 * section). Reuses `GoalFramePlacement` unchanged (same testids, zone
 * highlight/naming, keyboard mechanics, touch dedupe) and
 * `useRefinementChips`'s own `handlePlacementTap`/`placedPlacement`/
 * `dismissChipPanel` — no new PUT mechanism, no new eligibility rule, no
 * change to `data.goal_placement`.
 *
 * The scrim (`goal-placement-scrim`) covers the PITCH only (a sibling inside
 * `TacticalField`'s own `overlay` slot, `absolute inset-0` against the
 * pitch's own relative container) — not the whole page: clock, referee and
 * quick-action controls outside the pitch stay live. `TacticalField.tsx`'s
 * own `overlayBlocksPitch` boolean (threaded from `ActionStage.tsx` via
 * `PlayerSelectorPanel.tsx`, the SAME eligibility condition above) is what
 * actually makes token taps/coordinate taps under the scrim inert — this
 * component only needs to swallow its own clicks so they never bubble to
 * the pitch's `onClick`/`onPointerUp`, which `TacticalField.tsx`'s
 * pre-existing `overlay` wrapper already does (`stopPropagation` around the
 * whole slot), so no propagation handling is needed here either.
 *
 * EPIC-98 (item 5/AC5, client wave 2026-09-26): for an own goal
 * (`data.own_goal === true`) the header no longer shows the generic "Goal"
 * result — it names the CONCEDING player's own goal instead (a team crest
 * plus "Autogol · arco propio de <team> · <player>" / "Own goal · <team>'s
 * own goal · <player>"), since the benefiting team already shows up
 * everywhere else (score, "(AG)" shot-map ball) and this header was the one
 * place an operator couldn't tell whose net it actually was. The conceding
 * player's team is resolved from `event.player_id` against BOTH match
 * rosters (`utils/ownGoalTeam.ts`'s `findPlayerTeam`) — `event.team_id` is
 * always the BENEFITING team for an own goal, never the conceding one. No
 * taker-correction control exists for this event shape (that's Penalty-
 * only, below), frame geometry and `data.goal_placement` are unchanged, and
 * Skip still dispatches nothing.
 *
 * ui-judge round 1 (EPIC-98, defect 2): the CARD (dialog) is now a fixed
 * viewport layer, not pitch-local — but the SCRIM stays pitch-scoped,
 * unlike `PenaltyKickScreen.tsx`'s own (fully viewport-wide) fix. Split
 * deliberately, not by oversight:
 * - The card used to render `absolute inset-0` inside the pitch box's own
 *   `overlay` slot (`TacticalField.tsx`), which is only ~524px tall at
 *   1024x768/1180x820, putting Skip below the fold at `scrollY: 0`. It is
 *   now `createPortal`ed to `document.body` in its own `fixed inset-0`
 *   layer, capped at `max-h-[calc(100vh-24px)]` with its own
 *   `overflow-y-auto` — same fix as `PenaltyKickScreen.tsx`.
 * - The scrim is NOT portaled and keeps its original `absolute inset-0`
 *   sized against the pitch box's own `position: relative` ancestor
 *   (`TacticalField.tsx`'s `overlay` slot), exactly as before this round.
 *   `PMA-COCK-185.AC3`'s own pinned, already-tested guarantee — "controls
 *   outside the pitch (clock, referee, the quick-action wheel trigger)
 *   keep working while this overlay is open" (`e2e/logger-goal-
 *   placement.spec.ts`, "a tap on the scrim closes the overlay..." clicks
 *   `btn-stop-clock` WHILE the overlay is open and asserts it actually
 *   toggles) — would regress if the scrim grew to cover the whole
 *   viewport: any click anywhere, including on the clock rail, would hit
 *   the scrim first and dismiss the overlay instead of reaching that
 *   control. `overlayBlocksPitch` (`ActionStage.tsx`/`TacticalField.tsx`,
 *   unchanged) already makes the PITCH itself inert while this overlay is
 *   open regardless of the scrim, so the scrim's only remaining job is the
 *   visual dim + "tap empty pitch space to dismiss" gesture — both of
 *   which only ever needed to cover the pitch, never the whole viewport.
 * Every existing testid, the Escape/focus-management behaviour, and the
 * own-goal header (above) are unchanged.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TFunction } from "i18next";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match, Player } from "../../types";
import { formatDisplayName } from "../../../../lib/nameFormat";
import TeamLogo from "../../../../components/TeamLogo";
import GoalFramePlacement from "./GoalFramePlacement";
import { findPlayerTeam } from "../../utils/ownGoalTeam";
import {
  formatClockMinute,
  ZONE_I18N_KEYS,
  ZONE_FALLBACK_LABELS,
  type GoalPlacement,
} from "../../utils/goalFrame";

// PMA-COCK-178 pattern (also used by GoalFramePlacement.tsx and
// TacticalField.tsx): a touch tap fires both `pointerup` and a trailing
// synthetic `click` — the scrim's own click handler must ignore the one
// immediately following a touch dismiss so a single tap never double-fires.
const TOUCH_DEDUPE_MS = 700;

// PMA-COCK-185 AC2: the placed marker stays visible for at least this long
// (a "confirmation flash") before the overlay closes — driven by the PUT
// promise settling, never a bare timer alone, so a slow (or failed) PUT
// still waits out this floor and a fast one never closes before it.
const MIN_CLOSE_DELAY_MS = 400;

const RESULT_LABEL_KEY: Record<string, string> = {
  Goal: "goal",
  Saved: "saved",
  OnTarget: "ontarget",
  OffTarget: "miss",
};

const RESULT_FALLBACK: Record<string, string> = {
  goal: "Goal",
  saved: "Save",
  woodwork: "Woodwork",
  ontarget: "On target",
  miss: "Missed",
};

/** Mirrors `ReviewView.tsx`'s own (unexported) `getPlayerName` — searches
 * both rosters since this overlay isn't scoped to one team the way
 * `GoalMouthMap.tsx` is. Returns "" (not the id) when the roster hasn't
 * loaded the player, so the header composition below can just drop it. */
const getPlayerDisplay = (
  playerId: string | undefined,
  match: Match,
): string => {
  if (!playerId) return "";
  const allPlayers = [...match.home_team.players, ...match.away_team.players];
  const player = allPlayers.find((p) => p.id === playerId);
  return player
    ? `#${player.jersey_number} ${formatDisplayName(player.full_name)}`
    : "";
};

/** Result label from outcome/shot_type BEFORE placement — the same
 * five-kind shape `GoalMouthMap.tsx`'s classification collapses to at
 * header time (placement hasn't happened yet, so `classifyPlacedShot`
 * can't run): Goal, Saved and OnTarget map onto their own kind key;
 * everything else (an off-target Direct shot or missed Penalty, the only
 * other eligible outcomes) is a miss.
 *
 * Reads its OWN `goalPlacementOverlay.result.*` locale block, not
 * `goalMouthMap.result.*` — PMA-COCK-198. The two used to share one key
 * tree; PMA-COCK-193's shot-map legend rename ("Fallado"→"Tiro fuera")
 * silently retitled this popup too, since it happens to render for the
 * same outcome. This overlay's wording is its own and must never move
 * again just because the shot-map legend's wording changes. */
const resultLabelFor = (event: MatchEvent, t: TFunction<"logger">): string => {
  const outcome = event.data?.outcome;
  const key =
    (typeof outcome === "string" && RESULT_LABEL_KEY[outcome]) || "miss";
  return t(`goalPlacementOverlay.result.${key}`, RESULT_FALLBACK[key]);
};

export interface GoalPlacementOverlayProps {
  /** The confirmed Shot event driving this overlay — `isPlacementEligible`
   * has already gated whether this component is even mounted (see
   * `ActionStage.tsx`); this component does not re-check eligibility. */
  event: MatchEvent;
  match: Match;
  placedPlacement: GoalPlacement | null;
  onPlace: (placement: GoalPlacement) => void | Promise<void>;
  onDismiss: () => void;
  /** PMA-COCK-216 brief 15 (EPIC-73): the credited team's on-field roster,
   * for the penalty-taker correction control below — empty/omitted for any
   * non-penalty placement (DirectShot), which never renders that control at
   * all (there is no attribution ambiguity to correct there). */
  takerCandidates?: Player[];
  /** The player currently credited with this Shot — `event.player_id` by
   * default (pre-filled by `useActionFlow.ts`'s merged Penalty flow), or
   * whatever the operator last picked from the correction control. */
  currentTakerId?: string;
  /** PMA-COCK-216 brief 15: fires a `player_id` correction PUT on the
   * already-confirmed event (`ActionStage.tsx`'s `handleChangeTaker`) —
   * same best-effort policy as `onPlace`. */
  onChangeTaker?: (playerId: string) => void | Promise<void>;
  t: TFunction<"logger">;
}

const GoalPlacementOverlay = ({
  event,
  match,
  placedPlacement,
  onPlace,
  onDismiss,
  takerCandidates,
  currentTakerId,
  onChangeTaker,
  t,
}: GoalPlacementOverlayProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const lastScrimTouchDismissAt = useRef(0);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPlacing, setIsPlacing] = useState(false);

  const headingId = "goal-placement-overlay-title";

  // AC5: focus moves into the overlay (the frame itself) on open, and back
  // to whatever held it before, on close/unmount.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const frameEl = cardRef.current?.querySelector<HTMLElement>(
      '[data-testid="goal-frame-placement"]',
    );
    frameEl?.focus();
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      const previously = previouslyFocusedRef.current;
      if (previously && document.contains(previously)) {
        previously.focus();
      }
    };
  }, []);

  // Escape closes regardless of which element inside the overlay (if any)
  // currently holds focus.
  useEffect(() => {
    const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onDismiss();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onDismiss]);

  // AC2: place -> PUT (via onPlace, already wired to the same
  // handlePlacementTap the retired panel used), close once that promise
  // settles AND at least MIN_CLOSE_DELAY_MS has elapsed — whichever is
  // later. A second tap while closing is a no-op (frame is also
  // `disabled` below).
  const handlePlace = useCallback(
    (placement: GoalPlacement) => {
      if (isPlacing) return;
      setIsPlacing(true);
      const minDelay = new Promise<void>((resolve) => {
        closeTimerRef.current = setTimeout(resolve, MIN_CLOSE_DELAY_MS);
      });
      Promise.resolve(onPlace(placement))
        .catch(() => {
          // best-effort: same silent error policy as the PUT itself
          // (PMA-COCK-049 AC5) — the overlay still closes.
        })
        .then(() => minDelay)
        .then(() => {
          onDismiss();
        });
    },
    [isPlacing, onPlace, onDismiss],
  );

  const handleScrimPointerUp = useCallback(
    (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
      if (pointerEvent.pointerType !== "touch") return;
      lastScrimTouchDismissAt.current = Date.now();
      onDismiss();
    },
    [onDismiss],
  );

  const handleScrimClick = useCallback(() => {
    if (Date.now() - lastScrimTouchDismissAt.current < TOUCH_DEDUPE_MS) return;
    onDismiss();
  }, [onDismiss]);

  const resultLabel = resultLabelFor(event, t);
  // PMA-COCK-216 brief 15: `currentTakerId` wins over `event.player_id` so
  // the header stays consistent with a live taker correction — for every
  // non-penalty case `currentTakerId` is always exactly `event.player_id`
  // anyway (`ActionStage.tsx` never sets `changedTakerId` outside the
  // penalty-taker select), so this changes nothing there.
  const playerDisplay = getPlayerDisplay(
    currentTakerId ?? event.player_id,
    match,
  );
  const minute = formatClockMinute(event.match_clock);
  const headerText = [resultLabel, playerDisplay, minute]
    .filter(Boolean)
    .join(" · ");

  // EPIC-98 (item 5/AC5): an own goal's header names the CONCEDING player's
  // own goal, not a generic "Goal" result — the benefiting team already
  // shows up everywhere else (the score, the "(AG)" shot-map ball); this
  // header is the one place that says whose net it actually was. Own goals
  // never carry a taker-correction control (that's Penalty-only, above), so
  // the conceding player is always `event.player_id` — never
  // `currentTakerId`, which stays undefined for this event shape.
  const isOwnGoal = event.data?.own_goal === true;
  const concedingTeam = isOwnGoal
    ? findPlayerTeam(event.player_id, match)
    : null;
  const concedingPlayerDisplay = getPlayerDisplay(event.player_id, match);
  const ownGoalHeaderText = concedingTeam
    ? [
        t("goalPlacementOverlay.ownGoalLabel", "Own goal"),
        t("goalPlacementOverlay.ownGoalTeamLabel", "{{team}}'s own goal", {
          team: concedingTeam.short_name || concedingTeam.name,
        }),
        concedingPlayerDisplay,
        minute,
      ]
        .filter(Boolean)
        .join(" · ")
    : headerText;

  const placedZoneLabel = placedPlacement
    ? t(
        `goalFrame.zones.${ZONE_I18N_KEYS[placedPlacement.zone]}`,
        ZONE_FALLBACK_LABELS[placedPlacement.zone],
      )
    : null;
  const placedChipText = placedZoneLabel
    ? `${t("goalFrame.placedLabel", "Location")}: ${placedZoneLabel}`
    : t("goalFrame.placedChipEmpty", "Location: not recorded");

  // ui-judge round 1 (EPIC-98, defect 2): the scrim stays pitch-scoped, NOT
  // portaled — see this file's own module doc comment for why (PMA-COCK-185
  // AC3). `absolute inset-0` here sizes against the pitch box's own
  // `position: relative` `overlay` slot ancestor, unchanged from before this
  // round.
  const scrim = (
    <div className="absolute inset-0">
      <div
        data-testid="goal-placement-scrim"
        className="absolute inset-0 bg-slate-950/70"
        onClick={handleScrimClick}
        onPointerUp={handleScrimPointerUp}
      />
    </div>
  );

  const card = (
    // ui-judge round 1 (EPIC-98, defect 2): `fixed inset-0`, portaled to
    // `document.body` below — only the CARD moves to the viewport, the
    // scrim (above) stays pitch-scoped. `z-[9999]` matches the wheel's/
    // PenaltyKickScreen's own established "float above everything"
    // convention.
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 pointer-events-none">
      <div
        ref={cardRef}
        data-testid="goal-placement-overlay"
        role="dialog"
        aria-modal="false"
        aria-labelledby={headingId}
        aria-label={t("goalPlacementOverlay.dialogLabel", "Ball placement")}
        className="pointer-events-auto flex max-h-[calc(100vh-24px)] w-[min(560px,92vw)] flex-col gap-2 overflow-y-auto rounded-xl border border-slate-700 bg-slate-800 p-3 shadow-2xl"
      >
        <h2
          id={headingId}
          className="flex items-center gap-2 text-sm font-semibold text-slate-100"
        >
          {isOwnGoal && concedingTeam && (
            <TeamLogo
              team={concedingTeam}
              size="xs"
              className="shrink-0"
              testId="goal-placement-owngoal-crest"
            />
          )}
          <span className="min-w-0 flex-1 truncate">
            {isOwnGoal ? ownGoalHeaderText : headerText}
          </span>
        </h2>
        <p
          data-testid="goal-placement-overlay-instruction"
          className="text-xs font-semibold uppercase tracking-wide text-slate-300"
        >
          {t("goalFrame.instruction", "Tap where the ball went")}
        </p>
        {/* PMA-COCK-216 brief 15 (EPIC-73): visible ONLY for a Penalty
              dispatch — a DirectShot/generic shot's `player_id` is exactly
              who the operator tapped to start the flow, no default, nothing
              to correct here. Deliberately its own bordered row, ABOVE the
              goal frame rather than overlapping it, and a <select> rather
              than a single tap target — the lead's own two requirements:
              the pre-filled taker must be visible (the label + the
              select's own displayed value both name them), and the control
              must not be easy to hit by accident (a select needs an
              explicit open-then-choose gesture, not a stray tap while the
              operator's hand is going for the frame). */}
        {event.data?.shot_type === "Penalty" &&
          takerCandidates &&
          takerCandidates.length > 0 && (
            <div
              data-testid="goal-placement-taker-row"
              className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-900/60 px-3 py-2"
            >
              <label
                htmlFor="goal-placement-taker-select"
                className="text-xs font-semibold uppercase tracking-wide text-slate-300"
              >
                {t("goalPlacementOverlay.takerLabel", "Penalty taker")}
              </label>
              <select
                id="goal-placement-taker-select"
                data-testid="goal-placement-taker-select"
                value={currentTakerId ?? ""}
                disabled={isPlacing}
                onChange={(changeEvent) =>
                  onChangeTaker?.(changeEvent.target.value)
                }
                aria-label={t(
                  "goalPlacementOverlay.changeTaker",
                  "Change penalty taker",
                )}
                className="flex-1 truncate rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm font-semibold text-slate-100"
              >
                {takerCandidates.map((player) => (
                  <option key={player.id} value={player.id}>
                    #{player.jersey_number}{" "}
                    {formatDisplayName(player.full_name)}
                  </option>
                ))}
              </select>
            </div>
          )}
        <GoalFramePlacement
          value={placedPlacement}
          onPlace={handlePlace}
          source="live"
          outcome={event.data?.outcome}
          disabled={isPlacing}
          t={t}
        />
        <span
          data-testid="goal-frame-placed-chip"
          className="chip placed self-start rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-medium text-cyan-700"
        >
          {placedChipText}
        </span>
        <button
          type="button"
          data-testid="goal-placement-overlay-skip"
          onClick={onDismiss}
          className="inline-flex h-11 items-center self-start rounded-lg border border-slate-500 bg-slate-700 px-5 text-sm font-medium text-slate-100 transition-colors hover:bg-slate-600"
        >
          {t("goalPlacementOverlay.skip", "Skip")}
        </button>
      </div>
    </div>
  );

  // ui-judge round 1 (EPIC-98, defect 2): only the CARD is portalled to
  // document.body (same reasoning as QuickActionMenu.tsx's/
  // PenaltyKickScreen.tsx's own fixed layers — a `fixed inset-0` layer
  // rendered in place could still be re-anchored by an ancestor's own CSS
  // `transform`; a portal sidesteps the question entirely). The scrim stays
  // exactly where it was, rendered in place inside the pitch box's own
  // `overlay` slot — see this file's own module doc comment for why.
  return (
    <>
      {scrim}
      {createPortal(card, document.body)}
    </>
  );
};

export default GoalPlacementOverlay;
